/**
 * API Keys Management Page
 */

import { useEffect, useState } from 'react';
import { apiClient } from '../api/client';
import type { ApiKey, CreateApiKeyInput, User } from '../api/types';
import { Card } from '../components/Card';
import { Button } from '../components/Button';
import { Input } from '../components/Input';
import { Modal, ConfirmModal } from '../components/Modal';
import {
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableHeaderCell,
  TableCell,
  EmptyState,
  Badge,
} from '../components/Table';
import { Plus, Copy, Check, Trash2, Eye, RefreshCw } from 'lucide-react';
import { format } from 'date-fns';

export function ApiKeysPage() {
  const [isLoading, setIsLoading] = useState(true);
  const [keys, setKeys] = useState<ApiKey[]>([]);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isViewOpen, setIsViewOpen] = useState(false);
  const [isRevokeOpen, setIsRevokeOpen] = useState(false);
  const [selectedKey, setSelectedKey] = useState<ApiKey | null>(null);
  const [newKeyResult, setNewKeyResult] = useState<{ key: ApiKey; plainKey: string } | null>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    loadKeys();
  }, []);

  const loadKeys = async () => {
    setIsLoading(true);
    try {
      const res = await apiClient.listKeys({ includeExpired: true });
      setKeys(res.keys);
    } catch (err) {
      console.error('Failed to load keys:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateKey = async (input: CreateApiKeyInput) => {
    try {
      const res = await apiClient.createKey(input);
      setNewKeyResult({ key: res.apiKey, plainKey: res.plainKey });
      setIsCreateOpen(false);
      loadKeys();
    } catch (err) {
      console.error('Failed to create key:', err);
      throw err;
    }
  };

  const handleRevokeKey = async () => {
    if (!selectedKey) return;
    try {
      await apiClient.revokeKey(selectedKey.uuid);
      setIsRevokeOpen(false);
      setSelectedKey(null);
      loadKeys();
    } catch (err) {
      console.error('Failed to revoke key:', err);
    }
  };

  const copyToClipboard = async (text: string) => {
    await navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="p-8 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">API Keys</h1>
          <p className="text-gray-500 mt-1">
            Manage API keys for authenticating with vg-mem
          </p>
        </div>
        <div className="flex gap-3">
          <Button variant="secondary" onClick={loadKeys}>
            <RefreshCw className="w-4 h-4" />
            Refresh
          </Button>
          <Button onClick={() => setIsCreateOpen(true)}>
            <Plus className="w-4 h-4" />
            Create Key
          </Button>
        </div>
      </div>

      {/* Keys Table */}
      <Card>
        <Table>
          <TableHead>
            <TableRow>
              <TableHeaderCell>Name</TableHeaderCell>
              <TableHeaderCell>Key Prefix</TableHeaderCell>
              <TableHeaderCell>Tenant</TableHeaderCell>
              <TableHeaderCell>User</TableHeaderCell>
              <TableHeaderCell>Status</TableHeaderCell>
              <TableHeaderCell align="right">Requests</TableHeaderCell>
              <TableHeaderCell>Last Used</TableHeaderCell>
              <TableHeaderCell align="right">Actions</TableHeaderCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <tr>
                <td colSpan={8} className="px-6 py-12 text-center">
                  Loading...
                </td>
              </tr>
            ) : keys.length === 0 ? (
              <EmptyState message="No API keys found" colSpan={8} />
            ) : (
              keys.map((key) => (
                <TableRow key={key.uuid}>
                  <TableCell className="font-medium">{key.name}</TableCell>
                  <TableCell>
                    <code className="text-sm bg-gray-100 px-2 py-0.5 rounded">
                      {key.keyPrefix}...
                    </code>
                  </TableCell>
                  <TableCell>{key.tenantId}</TableCell>
                  <TableCell>
                    <div>
                      <p className="font-medium">{key.userName}</p>
                      <p className="text-gray-500 text-xs">{key.userEmail}</p>
                    </div>
                  </TableCell>
                  <TableCell>
                    {key.isActive ? (
                      key.expiresAt && new Date(key.expiresAt) < new Date() ? (
                        <Badge variant="warning">Expired</Badge>
                      ) : (
                        <Badge variant="success">Active</Badge>
                      )
                    ) : (
                      <Badge variant="danger">Revoked</Badge>
                    )}
                  </TableCell>
                  <TableCell align="right">
                    {key.requestCount.toLocaleString()}
                  </TableCell>
                  <TableCell>
                    {key.lastUsedAt
                      ? format(new Date(key.lastUsedAt), 'MMM d, yyyy HH:mm')
                      : 'Never'}
                  </TableCell>
                  <TableCell align="right">
                    <div className="flex justify-end gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setSelectedKey(key);
                          setIsViewOpen(true);
                        }}
                      >
                        <Eye className="w-4 h-4" />
                      </Button>
                      {key.isActive && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setSelectedKey(key);
                            setIsRevokeOpen(true);
                          }}
                        >
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Card>

      {/* Create Key Modal */}
      <CreateKeyModal
        isOpen={isCreateOpen}
        onClose={() => setIsCreateOpen(false)}
        onCreate={handleCreateKey}
      />

      {/* View Key Modal */}
      {selectedKey && (
        <Modal
          isOpen={isViewOpen}
          onClose={() => {
            setIsViewOpen(false);
            setSelectedKey(null);
          }}
          title="API Key Details"
          size="lg"
        >
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm text-gray-500">Name</label>
                <p className="font-medium">{selectedKey.name}</p>
              </div>
              <div>
                <label className="text-sm text-gray-500">Tenant ID</label>
                <p className="font-medium">{selectedKey.tenantId}</p>
              </div>
              <div>
                <label className="text-sm text-gray-500">User</label>
                <p className="font-medium">{selectedKey.userName}</p>
                <p className="text-sm text-gray-500">{selectedKey.userEmail}</p>
              </div>
              <div>
                <label className="text-sm text-gray-500">Organization</label>
                <p className="font-medium">{selectedKey.userOrg || '-'}</p>
              </div>
              <div>
                <label className="text-sm text-gray-500">Created</label>
                <p className="font-medium">
                  {format(new Date(selectedKey.createdAt), 'MMM d, yyyy HH:mm')}
                </p>
              </div>
              <div>
                <label className="text-sm text-gray-500">Expires</label>
                <p className="font-medium">
                  {selectedKey.expiresAt
                    ? format(new Date(selectedKey.expiresAt), 'MMM d, yyyy HH:mm')
                    : 'Never'}
                </p>
              </div>
              <div>
                <label className="text-sm text-gray-500">Requests</label>
                <p className="font-medium">{selectedKey.requestCount.toLocaleString()}</p>
              </div>
              <div>
                <label className="text-sm text-gray-500">Rate Limit</label>
                <p className="font-medium">
                  {selectedKey.rateLimit ? `${selectedKey.rateLimit}/min` : 'Unlimited'}
                </p>
              </div>
            </div>
            {selectedKey.allowedIps && selectedKey.allowedIps.length > 0 && (
              <div>
                <label className="text-sm text-gray-500">Allowed IPs</label>
                <div className="flex flex-wrap gap-2 mt-1">
                  {selectedKey.allowedIps.map((ip) => (
                    <Badge key={ip} variant="gray">{ip}</Badge>
                  ))}
                </div>
              </div>
            )}
          </div>
        </Modal>
      )}

      {/* Revoke Confirmation Modal */}
      <ConfirmModal
        isOpen={isRevokeOpen}
        onClose={() => {
          setIsRevokeOpen(false);
          setSelectedKey(null);
        }}
        onConfirm={handleRevokeKey}
        title="Revoke API Key"
        message={`Are you sure you want to revoke "${selectedKey?.name}"? This action cannot be undone.`}
        confirmText="Revoke Key"
        variant="danger"
      />

      {/* New Key Created Modal */}
      {newKeyResult && (
        <Modal
          isOpen={!!newKeyResult}
          onClose={() => setNewKeyResult(null)}
          title="API Key Created"
          size="md"
        >
          <div className="space-y-4">
            <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
              <p className="text-sm text-yellow-800">
                <strong>Important:</strong> Copy your API key now. You won't be able to see it again!
              </p>
            </div>
            <div>
              <label className="text-sm text-gray-500">API Key</label>
              <div className="flex items-center gap-2 mt-1">
                <code className="flex-1 p-3 bg-gray-100 rounded-lg text-sm font-mono break-all">
                  {newKeyResult.plainKey}
                </code>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => copyToClipboard(newKeyResult.plainKey)}
                >
                  {copied ? (
                    <Check className="w-4 h-4 text-green-500" />
                  ) : (
                    <Copy className="w-4 h-4" />
                  )}
                </Button>
              </div>
            </div>
            <div className="flex justify-end">
              <Button onClick={() => setNewKeyResult(null)}>Done</Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

// Create Key Form Modal
function CreateKeyModal({
  isOpen,
  onClose,
  onCreate,
}: {
  isOpen: boolean;
  onClose: () => void;
  onCreate: (input: CreateApiKeyInput) => Promise<void>;
}) {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [users, setUsers] = useState<User[]>([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState<string>('');
  const [form, setForm] = useState<CreateApiKeyInput>({
    tenantId: 'default',
    name: '',
    userId: '',
    userEmail: '',
    userName: '',
    userOrg: '',
  });

  // Load users when modal opens
  useEffect(() => {
    if (isOpen) {
      setUsersLoading(true);
      apiClient
        .listUsers()
        .then((res) => setUsers(res.users))
        .catch((err) => console.error('Failed to load users:', err))
        .finally(() => setUsersLoading(false));
    }
  }, [isOpen]);

  // Handle user selection from dropdown
  const handleUserSelect = (userId: string) => {
    setSelectedUserId(userId);
    if (userId) {
      const user = users.find((u) => u.uuid === userId);
      if (user) {
        setForm({
          ...form,
          userId: user.uuid,
          userEmail: user.email,
          userName: user.name,
          userOrg: user.org || '',
          tenantId: user.tenantId,
        });
      }
    } else {
      // Clear user fields if "manual entry" selected
      setForm({
        ...form,
        userId: '',
        userEmail: '',
        userName: '',
        userOrg: '',
      });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);
    try {
      await onCreate(form);
      setForm({
        tenantId: 'default',
        name: '',
        userId: '',
        userEmail: '',
        userName: '',
        userOrg: '',
      });
      setSelectedUserId('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create key');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Create API Key" size="md">
      <form onSubmit={handleSubmit} className="space-y-4">
        <Input
          label="Key Name"
          value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
          placeholder="e.g., Production API"
          required
        />

        {/* User Selection */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Select User
          </label>
          <select
            value={selectedUserId}
            onChange={(e) => handleUserSelect(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
            disabled={usersLoading}
          >
            <option value="">
              {usersLoading ? 'Loading users...' : '-- Select a user or enter manually --'}
            </option>
            {users.map((user) => (
              <option key={user.uuid} value={user.uuid}>
                {user.name} ({user.email})
              </option>
            ))}
          </select>
          {users.length === 0 && !usersLoading && (
            <p className="mt-1 text-sm text-gray-500">
              No users found. <a href="/users" className="text-blue-600 hover:underline">Add a user</a> first or enter details manually.
            </p>
          )}
        </div>

        <Input
          label="Tenant ID"
          value={form.tenantId}
          onChange={(e) => setForm({ ...form, tenantId: e.target.value })}
          placeholder="e.g., default"
          required
        />
        <div className="grid grid-cols-2 gap-4">
          <Input
            label="User ID"
            value={form.userId}
            onChange={(e) => setForm({ ...form, userId: e.target.value })}
            placeholder="e.g., user-123"
            required
            disabled={!!selectedUserId}
          />
          <Input
            label="User Name"
            value={form.userName}
            onChange={(e) => setForm({ ...form, userName: e.target.value })}
            placeholder="e.g., John Doe"
            required
            disabled={!!selectedUserId}
          />
        </div>
        <Input
          label="User Email"
          type="email"
          value={form.userEmail}
          onChange={(e) => setForm({ ...form, userEmail: e.target.value })}
          placeholder="e.g., john@example.com"
          required
          disabled={!!selectedUserId}
        />
        <Input
          label="Organization (optional)"
          value={form.userOrg || ''}
          onChange={(e) => setForm({ ...form, userOrg: e.target.value })}
          placeholder="e.g., Acme Inc"
          disabled={!!selectedUserId}
        />

        {error && (
          <div className="p-3 bg-red-50 text-red-700 rounded-lg text-sm">
            {error}
          </div>
        )}

        <div className="flex justify-end gap-3 pt-4">
          <Button type="button" variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" isLoading={isLoading}>
            Create Key
          </Button>
        </div>
      </form>
    </Modal>
  );
}
